/* eslint-disable react/no-array-index-key */
import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { DualFeatureCardsConfigProps } from 'components/DualFeatureCards/config';

import { buildBackgroundUrl } from 'utils/cloudinary/buildBackgroundUrl';

const DualFeatureCards: FC<DualFeatureCardsConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { compactSpacing, image, leftCard, rightCard } = rest;
  // Compact single-line copy decouples the diamond background from the card size.
  // The min-height that the diamond needs lives on the GRID (matching the stacked
  // card heights the homepage used: 2*420+gap on mobile, 2*480+gap on tablet, one
  // 520 row on desktop), and the grid packs its content to the TOP. The cards
  // themselves hug their content with tight, balanced padding, so the leftover
  // space falls BELOW the cards and shows the diamond there instead of an empty
  // card tail. This keeps the background at the exact same dimensions in every
  // view, independent of the card spacing.
  // Non-compact stays identical to the homepage.
  const cardHeightClasses = compactSpacing ? '' : 'min-h-[420px] md:min-h-[480px] lg:min-h-[520px]';
  const cardSpacingClasses = compactSpacing
    ? 'pb-8 pt-12 sm:pb-10 sm:pt-14 md:pb-10 md:pt-14'
    : 'pb-10 pt-14 sm:pb-14 sm:pt-16 md:pb-16 md:pt-20';
  const wrapperClasses = twJoin('relative flex flex-col items-center pt-6 sm:pt-8', compactSpacing ? '' : 'h-full');
  const gridClasses = twJoin(
    'grid grid-cols-1 gap-8 md:gap-10 lg:grid-cols-2',
    compactSpacing ? 'min-h-[872px] items-start content-start md:min-h-[1000px] lg:min-h-[520px]' : 'items-stretch',
  );
  const featureListClasses = twJoin(
    'flex w-full flex-col',
    compactSpacing ? 'gap-3 sm:gap-4' : 'gap-8 sm:gap-10 lg:gap-12',
  );

  return (
    <Section {...sectionProps} sidePadding="none">
      <div className="relative w-full overflow-hidden">
        {image?.file?.url && (
          <div
            aria-hidden
            className="
      absolute inset-0 -z-10 bg-center bg-no-repeat
      opacity-50 transition-[background-size,opacity]
      duration-300 [background-size:440%] sm:[background-size:440%] md:[background-size:200%]
      lg:opacity-100 lg:[background-size:100%]
    "
            style={{ backgroundImage: `url(${buildBackgroundUrl(image.file.url)})` }}
          />
        )}

        <div className="relative z-10 mx-auto w-full max-w-screen-xl px-5 py-14 sm:px-8 sm:py-16 lg:px-12 lg:py-24">
          <div className={gridClasses}>
            {leftCard && (
              <ScrollFadeSection delay={0.1}>
                <div className={wrapperClasses}>
                  {leftCard.badge && (
                    <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2">
                      <div className="inline-flex h-11 items-center justify-center gap-2.5 rounded-[30px] bg-gray-900 px-8 outline outline-2 outline-offset-[-2px] outline-primary-300 sm:px-10">
                        {leftCard.badge.icon && (
                          <div className="relative inline-flex size-6 shrink-0 overflow-hidden">
                            <Icon icon={leftCard.badge.icon} className="size-6 text-primary-300" />
                          </div>
                        )}
                        <div className="whitespace-nowrap text-center text-sm font-bold leading-[24px] text-primary-1400">
                          {leftCard.badge.text}
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className={twJoin(
                      'flex h-auto w-full max-w-screen-sm flex-col gap-8 rounded-lg bg-dual-feature-left px-6 backdrop-blur-[5px] sm:gap-10 sm:px-10 md:gap-12 md:px-12',
                      cardSpacingClasses,
                      cardHeightClasses,
                    )}
                  >
                    <div className={featureListClasses}>
                      {leftCard.features?.map((feature, index) => (
                        <div key={index} className="inline-flex items-start gap-4 sm:gap-5">
                          <div className="size-6">
                            {feature.hasBullet && (
                              <Icon
                                icon="circle-check"
                                size={24}
                                className="size-full rounded-full stroke-2 text-primary-1000"
                              />
                            )}
                          </div>
                          <div className="inline-flex flex-1 flex-col">
                            <div className="text-md font-bold leading-[28px] text-white">{feature.title}</div>
                            {feature.description && (
                              <div className="text-sm leading-[24px] text-gray-300">{feature.description}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollFadeSection>
            )}

            {rightCard && (
              <ScrollFadeSection delay={0.2}>
                <div className={wrapperClasses}>
                  {rightCard.badge && (
                    <div className="absolute left-1/2 top-0 z-10 -translate-x-1/2">
                      <div className="inline-flex h-11 items-center justify-center gap-2.5 rounded-[30px] bg-gray-900 px-8 outline outline-2 outline-offset-[-2px] outline-primary-1300 sm:px-10">
                        {rightCard.badge.icon && (
                          <div className="relative inline-flex size-6 shrink-0 overflow-hidden">
                            <Icon icon={rightCard.badge.icon} className="size-6 text-primary-1300" />
                          </div>
                        )}
                        <div className="whitespace-nowrap text-center text-sm font-bold leading-[24px] text-primary-1300">
                          {rightCard.badge.text}
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className={twJoin(
                      'flex h-auto w-full max-w-screen-sm flex-col gap-8 rounded-lg bg-dual-feature-right px-6 backdrop-blur-[5px] sm:gap-10 sm:px-10 md:gap-12 md:px-12',
                      cardSpacingClasses,
                      cardHeightClasses,
                    )}
                  >
                    <div className={featureListClasses}>
                      {rightCard.features?.map((feature, index) => (
                        <div key={index} className="inline-flex items-start gap-4 sm:gap-5">
                          <div className="size-6">
                            {feature.hasBullet && (
                              <Icon
                                icon="circle-check"
                                size={24}
                                className="size-full rounded-full stroke-2 text-primary-1300"
                              />
                            )}
                          </div>
                          <div className="inline-flex flex-1 flex-col">
                            <div className="text-md font-bold leading-[28px] text-white">{feature.title}</div>
                            {feature.description && (
                              <div className="text-sm leading-[24px] text-gray-300">{feature.description}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollFadeSection>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default DualFeatureCards;
